import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiGet, apiPost, apiUpload } from '../../services/api'
import { useOnlineStatus } from '../../hooks/useOnlineStatus'
import { saveOfflineSubmission, saveOfflineAttachment } from '../../services/offline'
import {
  ArrowLeft,
  Camera,
  Image,
  FileText,
  Upload,
  CheckCircle,
  AlertCircle,
  Clock,
  Trash2,
  MapPin,
  Calendar,
  Users,
  Loader2,
} from 'lucide-react'
import LoadingSpinner from '../../components/common/LoadingSpinner'

interface Indicator {
  id: number
  code: string
  name: string
  target: number
  max_points: number
  points_per_unit: number
}

interface Period {
  id: number
  year: number
  month: number
  name?: string
  is_locked: boolean
}

interface Submission {
  id: number
  submission_date: string
  status: string
  notes?: string
  created_at: string
}

interface LocationData {
  latitude: number
  longitude: number
  accuracy: number
  address?: string
}

export default function C1SubmissionPage() {
  const navigate = useNavigate()
  const isOnline = useOnlineStatus()
  const photoInputRef = useRef<HTMLInputElement>(null)
  const cameraInputRef = useRef<HTMLInputElement>(null)
  const documentInputRef = useRef<HTMLInputElement>(null)

  const [indicator, setIndicator] = useState<Indicator | null>(null)
  const [activePeriod, setActivePeriod] = useState<Period | null>(null)
  const [submissions, setSubmissions] = useState<Submission[]>([])
  const [submissionCount, setSubmissionCount] = useState(0)
  const [calculatedPoints, setCalculatedPoints] = useState(0)

  // Files
  const [meetingPhoto, setMeetingPhoto] = useState<File | null>(null)
  const [photoPreview, setPhotoPreview] = useState<string | null>(null)
  const [momDocument, setMomDocument] = useState<File | null>(null)
  const [notes, setNotes] = useState('')

  // Location
  const [location, setLocation] = useState<LocationData | null>(null)
  const [locationLoading, setLocationLoading] = useState(false)
  const [locationError, setLocationError] = useState<string | null>(null)

  // UI State
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [showPhotoOptions, setShowPhotoOptions] = useState(false)

  // Auto-captured date
  const currentDate = new Date()
  const formattedDate = currentDate.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
  const submissionDate = currentDate.toISOString().split('T')[0]

  useEffect(() => {
    fetchData()
    // Auto-fetch location on page load
    fetchLocation()
  }, [])

  useEffect(() => {
    if (activePeriod && indicator) {
      fetchSubmissions()
    }
  }, [activePeriod, indicator])

  const fetchData = async () => {
    try {
      const indicators = await apiGet<Indicator[]>('/kpi/indicators', { is_active: true })
      const c1Indicator = indicators.find(i => i.code === 'C.1')
      if (c1Indicator) {
        setIndicator(c1Indicator)
      }

      const periodsData = await apiGet<Period[]>('/kpi/periods')
      const active = periodsData.find(p => !p.is_locked) || periodsData[0]
      setActivePeriod(active || null)
    } catch (err) {
      console.error('Failed to load data:', err)
      setError('Failed to load data. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchSubmissions = async () => {
    if (!activePeriod?.id || !indicator?.id) return

    try {
      try {
        const scoresData = await apiGet<{
          scores: Array<{ indicator: Indicator; submission_count: number; calculated_score: number }>
        }>('/kpi/scores', { period_id: activePeriod.id })

        const score = scoresData.scores.find(s => s.indicator.code === 'C.1')
        if (score) {
          setSubmissionCount(score.submission_count)
          setCalculatedPoints(score.calculated_score)
        }
      } catch (scoreErr) {
        console.error('Failed to load scores:', scoreErr)
      }

      const submissionsData = await apiGet<{ items: Submission[] }>('/kpi/submissions', {
        period_id: activePeriod.id,
        indicator_id: indicator.id,
        per_page: 10,
      })
      setSubmissions(submissionsData.items || [])
    } catch (err) {
      console.error('Failed to load submissions:', err)
    }
  }

  const fetchLocation = async () => {
    setLocationLoading(true)
    setLocationError(null)

    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser')
      setLocationLoading(false)
      return
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const locationData: LocationData = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        }

        // Try to get address from coordinates (reverse geocoding)
        if (isOnline) {
          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${position.coords.latitude}&lon=${position.coords.longitude}`
            )
            const data = await response.json()
            if (data.display_name) {
              locationData.address = data.display_name
            }
          } catch (err) {
            console.error('Failed to get address:', err)
          }
        }

        setLocation(locationData)
        setLocationLoading(false)
      },
      (error) => {
        let errorMessage = 'Failed to get location'
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location permission denied. Please enable location access.'
            break
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information unavailable.'
            break
          case error.TIMEOUT:
            errorMessage = 'Location request timed out.'
            break
        }
        setLocationError(errorMessage)
        setLocationLoading(false)
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    )
  }

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (selectedFile) {
      if (selectedFile.size > 16 * 1024 * 1024) {
        setError('Photo size must be less than 16MB')
        return
      }

      setMeetingPhoto(selectedFile)
      setError(null)
      setShowPhotoOptions(false)

      const reader = new FileReader()
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string)
      }
      reader.readAsDataURL(selectedFile)
    }
  }

  const handleDocumentSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (selectedFile) {
      if (selectedFile.size > 16 * 1024 * 1024) {
        setError('Document size must be less than 16MB')
        return
      }

      setMomDocument(selectedFile)
      setError(null)
    }
  }

  const handleCameraCapture = () => {
    cameraInputRef.current?.click()
    setShowPhotoOptions(false)
  }

  const handleGallerySelect = () => {
    photoInputRef.current?.click()
    setShowPhotoOptions(false)
  }

  const removePhoto = () => {
    setMeetingPhoto(null)
    setPhotoPreview(null)
  }

  const removeDocument = () => {
    setMomDocument(null)
  }

  const handleSubmit = async () => {
    if (!activePeriod || !indicator) {
      setError('No active period found')
      return
    }

    if (!meetingPhoto) {
      setError('Please capture or upload a meeting photo')
      return
    }

    if (!momDocument) {
      setError('Please upload the Minutes of Meeting document')
      return
    }

    if (!location) {
      setError('Location is required. Please enable location access and try again.')
      return
    }

    setIsSubmitting(true)
    setError(null)

    // Build notes with location data
    const locationNote = `Location: ${location.latitude.toFixed(6)}, ${location.longitude.toFixed(6)}${location.address ? ` (${location.address})` : ''}`
    const fullNotes = notes ? `${notes}\n\n${locationNote}` : locationNote

    try {
      if (isOnline) {
        const response = await apiPost<{ id: number }>('/kpi/submissions', {
          period_id: activePeriod.id,
          indicator_id: indicator.id,
          submission_date: submissionDate,
          value: 1,
          notes: fullNotes,
          metadata: {
            location: {
              latitude: location.latitude,
              longitude: location.longitude,
              accuracy: location.accuracy,
              address: location.address,
            },
            captured_at: currentDate.toISOString(),
          },
        })

        // Upload meeting photo
        if (response.id && meetingPhoto) {
          const photoFormData = new FormData()
          photoFormData.append('file', meetingPhoto)
          photoFormData.append('submission_id', String(response.id))
          photoFormData.append('file_type', 'meeting_photo')
          await apiUpload('/kpi/attachments', photoFormData)
        }

        // Upload MoM document
        if (response.id && momDocument) {
          const docFormData = new FormData()
          docFormData.append('file', momDocument)
          docFormData.append('submission_id', String(response.id))
          docFormData.append('file_type', 'mom_document')
          await apiUpload('/kpi/attachments', docFormData)
        }
      } else {
        // Offline submission - include location in notes since metadata isn't supported offline
        const offlineNotes = `${fullNotes}\n\nCaptured at: ${currentDate.toISOString()}`

        const clientId = await saveOfflineSubmission({
          client_id: `c1_${Date.now()}`,
          period_id: activePeriod.id,
          indicator_id: indicator.id,
          submission_date: submissionDate,
          value: 1,
          notes: offlineNotes,
          created_at: new Date().toISOString(),
        })

        await saveOfflineAttachment(clientId, meetingPhoto, meetingPhoto.name, meetingPhoto.type)
        await saveOfflineAttachment(clientId, momDocument, momDocument.name, momDocument.type)
      }

      setSuccess(true)

      // Reset form
      setTimeout(() => {
        setMeetingPhoto(null)
        setPhotoPreview(null)
        setMomDocument(null)
        setNotes('')
        setSuccess(false)
        fetchSubmissions()
        // Re-fetch location for next submission
        fetchLocation()
      }, 2000)
    } catch (err) {
      console.error('Submission failed:', err)
      setError('Failed to submit. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <div className="mobile-page flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  const progress = indicator ? Math.min((submissionCount / indicator.target) * 100, 100) : 0

  return (
    <div className="mobile-page bg-gray-50">
      {/* Hidden file inputs */}
      <input
        ref={photoInputRef}
        type="file"
        className="hidden"
        accept="image/*"
        onChange={handlePhotoSelect}
      />
      <input
        ref={cameraInputRef}
        type="file"
        className="hidden"
        accept="image/*"
        capture="environment"
        onChange={handlePhotoSelect}
      />
      <input
        ref={documentInputRef}
        type="file"
        className="hidden"
        accept=".pdf,.doc,.docx,.xls,.xlsx"
        onChange={handleDocumentSelect}
      />

      {/* Header */}
      <div className="bg-blue-500 px-4 pt-4 pb-4">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => navigate('/kpi')} className="mobile-back-btn">
            <ArrowLeft className="h-6 w-6 text-white" />
          </button>
          <div className="flex-1">
            <h1 className="text-white text-lg font-semibold">Minutes of Meeting</h1>
            <p className="text-white/80 text-xs">C.1 - Periodic Meeting</p>
          </div>
        </div>

        {/* Progress Card */}
        <div className="bg-white rounded-xl shadow-lg p-4">
          <div className="flex items-center gap-4">
            <div className="bg-blue-100 rounded-xl p-3">
              <Users className="h-8 w-8 text-blue-500" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold text-gray-900">
                  {submissionCount} / {indicator?.target || 15}
                </span>
                <span className="text-sm font-semibold text-blue-500">
                  {calculatedPoints.toFixed(2)} pts
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Max {indicator?.max_points || 10} points
              </p>
              <div className="mt-2 bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-500 rounded-full h-2 transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Form Section */}
      <div className="px-4 mt-4 pb-32">
        {/* Auto-captured Date */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
          <div className="flex items-center gap-3">
            <div className="bg-blue-100 rounded-lg p-2">
              <Calendar className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Submission Date (Auto)</p>
              <p className="font-medium text-gray-900">{formattedDate}</p>
            </div>
          </div>
        </div>

        {/* Auto-captured Location */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
          <div className="flex items-center gap-3">
            <div className={`rounded-lg p-2 ${location ? 'bg-green-100' : locationError ? 'bg-red-100' : 'bg-gray-100'}`}>
              {locationLoading ? (
                <Loader2 className="h-5 w-5 text-gray-500 animate-spin" />
              ) : (
                <MapPin className={`h-5 w-5 ${location ? 'text-green-500' : locationError ? 'text-red-500' : 'text-gray-500'}`} />
              )}
            </div>
            <div className="flex-1">
              <p className="text-xs text-gray-500">Location (Auto)</p>
              {locationLoading ? (
                <p className="text-sm text-gray-600">Getting location...</p>
              ) : location ? (
                <div>
                  <p className="font-medium text-gray-900 text-sm">
                    {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
                  </p>
                  {location.address && (
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{location.address}</p>
                  )}
                </div>
              ) : locationError ? (
                <p className="text-sm text-red-600">{locationError}</p>
              ) : (
                <p className="text-sm text-gray-600">Location not available</p>
              )}
            </div>
            {(locationError || !location) && !locationLoading && (
              <button
                onClick={fetchLocation}
                className="px-3 py-1.5 bg-blue-100 text-blue-600 text-xs font-medium rounded-lg"
              >
                Retry
              </button>
            )}
          </div>
        </div>

        {/* Meeting Photo Upload */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
          <label className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
            <Camera className="h-4 w-4" />
            Meeting Photo
            <span className="text-red-500">*</span>
          </label>

          {!meetingPhoto ? (
            <button
              onClick={() => setShowPhotoOptions(true)}
              className="mobile-upload-area"
            >
              <Camera className="h-10 w-10 text-gray-400 mb-2" />
              <span className="text-gray-600 font-medium">Capture Meeting Photo</span>
              <span className="text-gray-400 text-xs mt-1">
                Take a photo of the meeting in progress
              </span>
            </button>
          ) : (
            <div className="mobile-file-preview">
              {photoPreview && (
                <img src={photoPreview} alt="Meeting" className="mobile-file-image" />
              )}
              <div className="mobile-file-info">
                <p className="font-medium text-gray-900 truncate">{meetingPhoto.name}</p>
                <p className="text-xs text-gray-500">
                  {(meetingPhoto.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
              <button onClick={removePhoto} className="mobile-file-remove">
                <Trash2 className="h-5 w-5" />
              </button>
            </div>
          )}
        </div>

        {/* MoM Document Upload */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
          <label className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Minutes of Meeting Document
            <span className="text-red-500">*</span>
          </label>

          {!momDocument ? (
            <button
              onClick={() => documentInputRef.current?.click()}
              className="mobile-upload-area"
            >
              <Upload className="h-10 w-10 text-gray-400 mb-2" />
              <span className="text-gray-600 font-medium">Upload MoM Document</span>
              <span className="text-gray-400 text-xs mt-1">
                PDF, Word, or Excel file (max 16MB)
              </span>
            </button>
          ) : (
            <div className="mobile-file-preview">
              <div className="mobile-file-icon">
                <FileText className="h-12 w-12 text-blue-500" />
              </div>
              <div className="mobile-file-info">
                <p className="font-medium text-gray-900 truncate">{momDocument.name}</p>
                <p className="text-xs text-gray-500">
                  {(momDocument.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
              <button onClick={removeDocument} className="mobile-file-remove">
                <Trash2 className="h-5 w-5" />
              </button>
            </div>
          )}
        </div>

        {/* Notes */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
          <label className="text-sm font-medium text-gray-700 mb-2 block">
            Notes (Optional)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add meeting notes, attendees, or topics discussed..."
            rows={3}
            className="mobile-textarea"
          />
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4 flex items-center gap-3">
            <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
            <p className="text-sm text-green-700">
              {isOnline ? 'MoM submitted successfully!' : 'Saved offline. Will sync when online.'}
            </p>
          </div>
        )}

        {/* Offline Warning */}
        {!isOnline && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-4 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-yellow-500 flex-shrink-0" />
            <p className="text-sm text-yellow-700">
              You're offline. Submission will be saved locally and synced later.
            </p>
          </div>
        )}

        {/* Recent Submissions */}
        {submissions.length > 0 && (
          <div className="mt-6">
            <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Recent Submissions
            </h3>
            <div className="space-y-2">
              {submissions.slice(0, 5).map((sub) => (
                <div key={sub.id} className="bg-white rounded-xl p-3 shadow-sm flex items-center gap-3">
                  <div className="bg-blue-100 rounded-lg p-2">
                    <Users className="h-5 w-5 text-blue-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">
                      {new Date(sub.submission_date).toLocaleDateString('en-US', {
                        weekday: 'short',
                        day: 'numeric',
                        month: 'short',
                      })}
                    </p>
                    {sub.notes && (
                      <p className="text-xs text-gray-500 truncate">{sub.notes}</p>
                    )}
                  </div>
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                    sub.status === 'approved' ? 'bg-green-100 text-green-700' :
                    sub.status === 'rejected' ? 'bg-red-100 text-red-700' :
                    'bg-blue-100 text-blue-700'
                  }`}>
                    {sub.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Submit Button - Fixed at bottom */}
      <div className="mobile-submit-container">
        <button
          onClick={handleSubmit}
          disabled={isSubmitting || !meetingPhoto || !momDocument || !location}
          className="mobile-submit-btn bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300"
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin" />
              Submitting...
            </span>
          ) : (
            'Submit C.1'
          )}
        </button>
      </div>

      {/* Photo Options Modal */}
      {showPhotoOptions && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-50"
            onClick={() => setShowPhotoOptions(false)}
          />
          <div className="mobile-upload-modal">
            <div className="text-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Meeting Photo</h3>
              <p className="text-sm text-gray-500">Choose how to add photo</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button onClick={handleCameraCapture} className="mobile-upload-option">
                <Camera className="h-8 w-8 text-blue-500 mb-2" />
                <span className="text-sm font-medium">Camera</span>
              </button>
              <button onClick={handleGallerySelect} className="mobile-upload-option">
                <Image className="h-8 w-8 text-green-500 mb-2" />
                <span className="text-sm font-medium">Gallery</span>
              </button>
            </div>
            <button
              onClick={() => setShowPhotoOptions(false)}
              className="w-full mt-4 py-3 text-gray-600 font-medium"
            >
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  )
}
